import React, {
    memo,
    useMemo,
    useState,
    useCallback,
    useRef,
    useEffect,
    Fragment,
    useContext,
} from 'react';
import { createUseStyles, Loader } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { getList, searchValuesByIds } from 'lib/services/valueLists';
import { showErrorToast } from 'lib/errors';
import { getUniqueKeys, sort } from 'utils/array';
import ImportContext from './../../ImportContext';
import { MATCHING_VALUES } from '../../constants';

import SectionList from 'components/SectionList';

// Custom components
import FuzzyFieldFormatter from '../formatters/FuzzyFieldFormatter';
import InnerClassFormatter from '../formatters/InnerClassFormatter';
import SwitchFormatter from '../formatters/SwitchFormatter';
import ExcelField from './headerComponents/ExcelField';
import SageField from './headerComponents/SageField';

import styles from './styles';
const useStyles = createUseStyles(styles, 'MatchingList');

const MatchingList = ({
    data,
    fields,
    fieldTable,
    canCreate,
    onMatch,
    hideMatchedFlields,
    matchBy,
}) => {
    const classes = useStyles();
    const { t, language: lang } = useWeb5Context();
    const { language: externalLanguage } = useContext(ImportContext);
    const language = externalLanguage || lang;
    const [isLoading, setIsLoading] = useState(false);
    const options = useRef(null);
    const matchValues = useRef(fields.map((field) => data.extraInfo[field]));
    const matchOptions = useRef(
        fields.map((field) => ({
            label: field,
            value: data.extraInfo[field]?.toString(),
        })),
    );
    const [createAsNewOneObj, setCreateAsNewOne] = useState(
        fields.reduce((obj, current) => {
            obj[current] = false;
            return obj;
        }, {}),
    );

    useEffect(() => {
        if (options.current || isLoading) return;
        setIsLoading(true);
        const ids = matchValues.current.reduce((arr, el) => (el > 0 ? [...arr, el] : arr), []);
        Promise.all([
            searchValuesByIds({
                entity: fieldTable,
                ids,
                lang: language,
                searchType: parseInt(matchBy, 10),
            }),
            getList({ list: fieldTable, language, searchType: matchBy }),
        ])
            .then(([{ data: selectedOptions = [] }, { data: firstOptions = [] }]) => {
                options.current = sort(
                    getUniqueKeys(
                        [...Object.values(selectedOptions), ...firstOptions].map((item) => ({
                            label: item.description,
                            value: item.id,
                        })),
                        'value',
                    ),
                    'label',
                );
            })
            .catch((err) => {
                options.current = [];
                console.error(err);
                showErrorToast(t, err);
            })
            .finally(() => setIsLoading(false));
    }, [fieldTable, isLoading, language, t, matchBy, canCreate]);

    const onChangeMatch = useCallback(
        (index, value) => {
            matchValues.current[index] = value;
            onMatch(fields[index], value);
        },
        [fields, onMatch],
    );

    const onChangeCreateAsNew = useCallback(
        (index, value) => {
            matchValues.current[index] = value;
            onMatch(fields[index], value);
            setCreateAsNewOne({ ...createAsNewOneObj, [fields[index]]: value });
        },
        [fields, onMatch, createAsNewOneObj],
    );

    const listProps = useMemo(() => {
        let columns = [
            {
                field: 'name',
                headerName: t('cfm_label_your_values_xls'),
                resizable: true,
                cellRenderer: 'InnerClassFormatter',
                cellRendererParams: {
                    className: classes.matchingListCell,
                },
                headerComponent: 'ExcelField',
            },
            {
                field: 'nameField',
                headerName: t('cfm_label_fm_values'),
                resizable: true,
                cellRenderer: 'FuzzyFieldFormatter',
                cellRendererParams: {
                    className: classes.fmCell,
                    options: options.current,
                    matchOptions: matchOptions.current,
                    onChange: onChangeMatch,
                    hasValuesSync: false,
                    fieldTable,
                },
                headerComponent: 'SageField',
            },
        ];

        if (canCreate) {
            columns.push({
                field: 'createAsNewOne',
                headerName: t('cfm_label_new_value'),
                resizable: true,
                cellRenderer: 'SwitchFormatter',
                cellRendererParams: {
                    onChange: onChangeCreateAsNew,
                },
            });
        }

        return {
            className: classes.matchingList,
            isLoading,
            rows: fields
                .map((field, index) => ({
                    name: field,
                    nameField: data.extraInfo[field],
                    index,
                    id: index,
                    createAsNewOne: createAsNewOneObj[field],
                }))
                .filter((field) =>
                    hideMatchedFlields
                        ? field.nameField === MATCHING_VALUES.noValue ||
                          field.nameField === MATCHING_VALUES.creatAsNew
                        : true,
                ),
            columns,
            components: {
                ExcelField,
                SageField,
                InnerClassFormatter,
                FuzzyFieldFormatter,
                SwitchFormatter,
            },
            options: {
                suppressRowHoverHighlight: true,
                rowHeight: 40,
                headerHeight: 50,
            },
        };
    }, [
        classes.matchingList,
        classes.matchingListCell,
        classes.fmCell,
        t,
        onChangeMatch,
        canCreate,
        fieldTable,
        isLoading,
        fields,
        data.extraInfo,
        hideMatchedFlields,
        createAsNewOneObj,
        onChangeCreateAsNew,
    ]);

    return (
        <Fragment>
            {(isLoading || !options.current) && (
                <div className={classes.modalLoader}>
                    <Loader size="big" />
                </div>
            )}
            {!isLoading && options.current && <SectionList id="matching-list" {...listProps} />}
        </Fragment>
    );
};

export default memo(MatchingList);
